import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React from 'react';

import { useGetSettlementDetailsXLSX } from '@/modules/Settlements/apiHooks';
import {
  getSerializedXLSXData,
  getWorkerSerializedXLSXData,
} from '@/modules/Settlements/components/Details/shared/Header/ActionsButtons/XLSXSettlementAction/logic';
import { useGoogleSheets } from '@/modules/Settlements/components/Details/shared/Header/ActionsButtons/XLSXSettlementAction/useGoogleSheets';
import { ISettlements } from '@/modules/Settlements/types';
import { Button } from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import { BaseIcon } from '@/shared/components/atoms/IconButton/BaseIcon';
import routes from '@/shared/constant/routes';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { getLocalizedDate } from '@/shared/lib/dates';

import XLSXSettlementIcon from '~/images/shared/note.svg';

const XLSXSettlementAction = () => {
  const { t } = useTranslation('common');
  const { query, locale } = useRouter();
  const { openModal } = useModal();
  const { id } = query;

  const [store] = useStore((store) => store);
  const selectedAsset = store?.asset;
  const isDemoFarm = store?.isDemoFarm;
  const { data } = useGetSettlementDetailsXLSX({
    entityId: isDemoFarm ? undefined : selectedAsset?.id,
    id: id?.toString(),
    type: selectedAsset?.settlementLevel,
  });

  const serializedXLSXedData =
    selectedAsset?.settlementLevel === 2
      ? getWorkerSerializedXLSXData({
          data: data as ISettlements.IGetWorkerSettlementDetails,
          t,
          currency: selectedAsset?.currency || '',
          farm: selectedAsset?.name,
          language: locale ?? 'en',
        })
      : getSerializedXLSXData({
          data: data as ISettlements.IGetSettlementDetails,
          t,
          currency: selectedAsset?.currency || '',
          farm: selectedAsset?.name,
          language: locale ?? 'en',
        });

  const { exportToGoogleSheet, isLoading } = useGoogleSheets({
    fileName: `${t('settlement')}-${id} - ${getLocalizedDate({
      date: data?.issuanceDate,
      locale,
    })}`,
  });

  const handleClick = () => {
    if (isDemoFarm)
      openModal(<DemoCTA redirectRouteBeforeExit={routes.settlements} />);
    else exportToGoogleSheet(serializedXLSXedData);
  };

  return (
    <>
      <Button
        variant='filled'
        color='secondary'
        loading={isLoading}
        onClick={handleClick}
        className='flex items-center justify-center gap-8'
      >
        <>
          <BaseIcon size='sm'>
            <XLSXSettlementIcon />
          </BaseIcon>
          {t('download-xlsx')}
        </>
      </Button>
    </>
  );
};

export default XLSXSettlementAction;
